import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { getApiBaseUrl } from '../../utils/apiConfig';
import {
  extractUtmFromUrl,
  getMetaMatchParams,
  saveUtmToStorage,
  trackEvent,
  trackPageView,
} from '../../utils/analytics';
import './ExhibitionRegistrationPage.css';

const EXHIBITION_EVENT_TYPE = '2026_exhibition_booth';
const EXHIBITION_COUPON_CODE = 'TEACHER30';
const LANDING_PAGE = '/events/exhibition';

const DEFAULT_UTM = {
  utm_source: 'exhibition_qr',
  utm_medium: 'offline',
  utm_campaign: '2026_exhibition',
  utm_content: 'booth_qr',
};

const INITIAL_FORM = {
  name: '',
  organization: '',
  email: '',
  phone: '',
  privacyConsent: false,
  marketingConsent: false,
};

const BENEFIT_SUMMARY = [
  {
    title: 'PRO 30일 제공',
    description: 'TEACHER30 쿠폰으로 프리미엄 기능을 30일 동안 체험해요.',
  },
  {
    title: '추후 구매 시 최대 50% 할인',
    description: '체험 후 계속 쓰고 싶을 때 전시회 혜택가를 안내드려요.',
  },
];

const STEPS = [
  {
    id: 'intro',
    eyebrow: '현장 등록 EVENT',
    title: '30초만 등록하면\n혜택 2가지를 드려요',
    subtitle: '전시회에서 만난 선생님께만 드리는 왓퀴즈 현장 혜택이에요.',
    cta: '등록하러 가기',
  },
  {
    id: 'contact',
    eyebrow: '거의 다 왔어요',
    title: '혜택을 받을 정보를\n입력해주세요',
    subtitle: '쿠폰과 전시회 혜택 안내를 보내드리기 위한 최소 정보만 받아요.',
    cta: '동의하고 마무리하기',
  },
  {
    id: 'consent',
    eyebrow: '마지막 단계',
    title: '개인정보 이용에\n동의해주세요',
    subtitle: '현장 혜택 제공을 위해 필요한 동의예요.',
    cta: '현장 혜택 받기',
  },
];

const buildLeadEventId = () => (
  `exhibition_qr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

const ExhibitionRegistrationPage = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [utmParams, setUtmParams] = useState(DEFAULT_UTM);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [animating, setAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const startTimeRef = useRef(Date.now());

  const step = STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isContactStep = step.id === 'contact';
  const isConsentStep = step.id === 'consent';
  const progress = Math.round(((currentStep + 1) / STEPS.length) * 100);
  const couponUrl = useMemo(() => `/welcome?coupon=${EXHIBITION_COUPON_CODE}`, []);

  useEffect(() => {
    const urlUtm = extractUtmFromUrl() || {};
    const nextUtm = { ...DEFAULT_UTM, ...urlUtm };
    setUtmParams(nextUtm);
    saveUtmToStorage({
      ...nextUtm,
      landing_page: LANDING_PAGE,
    });

    trackPageView(LANDING_PAGE, '왓퀴즈 전시회 현장 등록');
    trackEvent('exhibition_registration_view', {
      landing_page: LANDING_PAGE,
      ...nextUtm,
    });

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
      window.fbq('track', 'ViewContent', {
        content_name: 'exhibition_booth_registration',
        content_type: 'offline_event_registration',
        landing_page: LANDING_PAGE,
        ...nextUtm,
      });
    }
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const validateContact = () => {
    if (!form.name.trim()) return '이름을 입력해주세요.';
    if (!form.organization.trim()) return '소속과 직책을 입력해주세요.';
    if (!form.email.trim()) return '이메일을 입력해주세요.';
    if (!form.phone.trim()) return '전화번호를 입력해주세요.';
    return '';
  };

  const validateConsent = () => {
    if (!form.privacyConsent) {
      return '현장 혜택 제공을 위해 개인정보 수집·이용 동의가 필요해요.';
    }
    return '';
  };

  const moveToStep = useCallback((nextStep, nextDirection) => {
    if (nextStep < 0 || nextStep >= STEPS.length || animating) return;

    setDirection(nextDirection);
    setAnimating(true);
    window.setTimeout(() => {
      setCurrentStep(nextStep);
      setAnimating(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 220);
  }, [animating]);

  const handleBack = () => {
    if (isFirstStep || isSubmitting) return;
    setErrorMessage('');
    moveToStep(currentStep - 1, 'backward');
  };

  const submitRegistration = async () => {
    if (isSubmitting) return;

    const contactError = validateContact();
    const consentError = validateConsent();
    if (contactError || consentError) {
      setErrorMessage(contactError || consentError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const leadEventId = buildLeadEventId();
    const trimmedForm = {
      name: form.name.trim(),
      organization: form.organization.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      privacyConsent: form.privacyConsent,
      marketingConsent: form.marketingConsent,
    };

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/user/pre-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedForm.name,
          organization: trimmedForm.organization,
          email: trimmedForm.email,
          phone: trimmedForm.phone,
          selectedPlan: 'teacher30',
          quantity: '1',
          message: '전시회 현장 QR 등록',
          eventType: EXHIBITION_EVENT_TYPE,
          coupon_code: EXHIBITION_COUPON_CODE,
          privacy_consent: trimmedForm.privacyConsent,
          marketing_consent: trimmedForm.marketingConsent,
          landing_page: LANDING_PAGE,
          benefit_tags: ['pro_30_days', 'discount_50'],
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          utm_content: utmParams.utm_content || null,
          event_id: leadEventId,
          funnel_data: {
            started_at: startTimeRef.current,
            completed_at: Date.now(),
            duration_ms: Date.now() - startTimeRef.current,
          },
          ...getMetaMatchParams(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || '등록을 완료하지 못했어요. 잠시 후 다시 시도해주세요.');
      }

      trackEvent('exhibition_registration_submit', {
        event_category: 'Conversion',
        event_label: EXHIBITION_EVENT_TYPE,
        value: 1,
        landing_page: LANDING_PAGE,
        event_id: leadEventId,
        duration_ms: Date.now() - startTimeRef.current,
        ...utmParams,
      });

      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'exhibition_booth_registration',
          content_category: EXHIBITION_EVENT_TYPE,
          landing_page: LANDING_PAGE,
          ...utmParams,
        }, { eventID: leadEventId });
      }

      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrorMessage(error.message || '등록 중 문제가 생겼어요. 입력 내용을 확인한 뒤 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isSubmitting || animating) return;

    if (isContactStep) {
      const validationError = validateContact();
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }
    }

    if (isConsentStep) {
      submitRegistration();
      return;
    }

    setErrorMessage('');
    moveToStep(currentStep + 1, 'forward');
  };

  const renderIntroStep = () => (
    <div className="exhibition-flow__benefit-summary" aria-label="현장 등록 혜택">
      {BENEFIT_SUMMARY.map((benefit, index) => (
        <article className="exhibition-flow__benefit-item" key={benefit.title}>
          <span aria-hidden="true">{index + 1}</span>
          <div>
            <h2>{benefit.title}</h2>
            <p>{benefit.description}</p>
          </div>
        </article>
      ))}
      <div className="exhibition-flow__notice-card">
        카드 등록 없이 쿠폰을 받을 수 있고, 전시회 전용 혜택가는 등록하신 연락처로 안내드려요.
      </div>
    </div>
  );

  const renderContactStep = () => (
    <div className="exhibition-flow__form">
      <div className="exhibition-flow__field">
        <label htmlFor="exhibition-name">이름</label>
        <input
          id="exhibition-name"
          type="text"
          required
          value={form.name}
          onChange={(event) => updateField('name', event.target.value)}
          placeholder="김왓퀴"
          autoComplete="name"
        />
      </div>

      <div className="exhibition-flow__field">
        <label htmlFor="exhibition-organization">소속 / 직책</label>
        <input
          id="exhibition-organization"
          type="text"
          required
          value={form.organization}
          onChange={(event) => updateField('organization', event.target.value)}
          placeholder="왓퀴즈초등학교 교사"
          autoComplete="organization"
        />
      </div>

      <div className="exhibition-flow__field">
        <label htmlFor="exhibition-email">이메일</label>
        <input
          id="exhibition-email"
          type="email"
          required
          value={form.email}
          onChange={(event) => updateField('email', event.target.value)}
          placeholder="abc@school.ac.kr"
          autoComplete="email"
        />
      </div>

      <div className="exhibition-flow__field">
        <label htmlFor="exhibition-phone">전화번호</label>
        <input
          id="exhibition-phone"
          type="tel"
          required
          value={form.phone}
          onChange={(event) => updateField('phone', event.target.value)}
          placeholder="010-0000-0000"
          autoComplete="tel"
        />
        <p>쿠폰과 전시회 혜택 안내를 위해 필요해요.</p>
      </div>
    </div>
  );

  const renderConsentStep = () => (
    <div className="exhibition-flow__consents">
      <label className={`exhibition-flow__consent ${form.privacyConsent ? 'exhibition-flow__consent--checked' : ''}`}>
        <input
          type="checkbox"
          checked={form.privacyConsent}
          onChange={(event) => updateField('privacyConsent', event.target.checked)}
        />
        <span>
          <strong>[필수] 개인정보 수집·이용에 동의해요</strong>
          현장 등록 혜택 제공, TEACHER30 쿠폰 안내, 문의 응대를 위해 필요한 정보만 수집해요.
        </span>
      </label>

      <div className="exhibition-flow__consent-detail" aria-label="개인정보 수집 이용 상세">
        <div>
          <strong>수집 항목</strong>
          <p>이름, 소속/직책, 이메일, 전화번호, 등록 일시, 유입 경로</p>
        </div>
        <div>
          <strong>이용 목적</strong>
          <p>현장 혜택 제공, 쿠폰 발송, 고객 문의 응대</p>
        </div>
        <div>
          <strong>보유 기간</strong>
          <p>이벤트 종료 및 혜택 제공 완료 후 1년 보관 후 파기. 단, 관계 법령상 보관 의무가 있으면 해당 기간 동안 보관해요.</p>
        </div>
        <div>
          <strong>동의 거부권</strong>
          <p>동의를 거부할 수 있지만, 이 경우 현장 혜택 제공이 어려워요.</p>
        </div>
        <a href="/privacy-policy" target="_blank" rel="noreferrer">
          개인정보처리방침 보기
        </a>
      </div>

      <label className={`exhibition-flow__consent ${form.marketingConsent ? 'exhibition-flow__consent--checked' : ''}`}>
        <input
          type="checkbox"
          checked={form.marketingConsent}
          onChange={(event) => updateField('marketingConsent', event.target.checked)}
        />
        <span>
          <strong>[선택] 광고성 정보 수신에 동의해요</strong>
          할인, 연수, 기능 업데이트 등 왓퀴즈 소식과 혜택 안내를 이메일 또는 문자로 받을 수 있어요.
        </span>
      </label>

      <div className="exhibition-flow__consent-note">
        선택 동의를 하지 않아도 PRO 30일 쿠폰은 받을 수 있어요.
      </div>
    </div>
  );

  const renderStepBody = () => {
    if (step.id === 'intro') return renderIntroStep();
    if (isContactStep) return renderContactStep();
    if (isConsentStep) return renderConsentStep();
    return null;
  };

  if (isSubmitted) {
    return (
      <main className="exhibition-flow">
        <Helmet>
          <title>전시회 등록 완료 | 왓퀴즈</title>
          <meta name="robots" content="noindex" />
        </Helmet>

        <section className="exhibition-flow__success">
          <div className="exhibition-flow__success-card" role="status" aria-live="polite">
            <div className="exhibition-flow__success-icon" aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="exhibition-flow__eyebrow">등록 완료</p>
            <h1>PRO 30일 쿠폰을<br />보내드릴게요</h1>
            <p>이메일과 문자로 `TEACHER30` 쿠폰 안내가 발송돼요.</p>
            <a className="exhibition-flow__primary" href={couponUrl}>
              30일 쿠폰 바로 받기
            </a>
            <div className="exhibition-flow__success-note">
              카드 등록 없이 30일 동안 바로 체험할 수 있어요.
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="exhibition-flow">
      <Helmet>
        <title>왓퀴즈 현장 등록 이벤트</title>
        <meta name="description" content="전시회 현장에서 왓퀴즈를 등록하고 PRO 30일 체험과 구매 할인 혜택을 받아보세요." />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href="https://whatquiz.co.kr/events/exhibition" />
      </Helmet>

      <section className="exhibition-flow__screen">
        <div className="exhibition-flow__container">
          <header className="exhibition-flow__top">
            <button
              type="button"
              className="exhibition-flow__back"
              onClick={handleBack}
              disabled={isFirstStep || isSubmitting}
              aria-label="이전 단계로 이동"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="exhibition-flow__counter">{currentStep + 1}/{STEPS.length}</span>
          </header>

          <div className="exhibition-flow__progress" aria-hidden="true">
            <div className="exhibition-flow__progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div className={`exhibition-flow__content exhibition-flow__content--${direction}${animating ? ' exhibition-flow__content--leaving' : ''}`}>
            <p className="exhibition-flow__eyebrow">{step.eyebrow}</p>
            <h1>{step.title}</h1>
            <p className="exhibition-flow__subtitle">{step.subtitle}</p>

            {renderStepBody()}
          </div>

          {errorMessage && (
            <div className="exhibition-flow__error" role="alert">
              {errorMessage}
            </div>
          )}

          <div className="exhibition-flow__bottom">
            <button
              type="button"
              className="exhibition-flow__primary"
              onClick={handleNext}
              disabled={isSubmitting || animating}
            >
              {isSubmitting ? '등록 중...' : step.cta}
            </button>
            <p>{isConsentStep ? '카드 등록 없이 쿠폰만 받을 수 있어요.' : '모바일에서 빠르게 끝낼 수 있어요.'}</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ExhibitionRegistrationPage;
